import React, { useEffect, useRef, useState } from 'react';
import { CheckCheck, Loader2, Quote, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { commsApi } from '@/utils/commsApi';
import { speak } from '@/utils/tts';
import { Button } from '@/components/ui/button';
import { TaskDraftCard } from './TaskDraftCard';

const toPayload = (d) => ({
  title: d.title.trim(),
  description: d.description.trim(),
  team_id: d.team_id,
  assigned_to: d.assigned_to || null,
  priority: d.priority || null,
  estimated_hours: d.estimated_hours === '' || d.estimated_hours == null ? null : Number(d.estimated_hours),
  subtasks: d.subtasks.map((s) => s.trim()).filter(Boolean),
  due_date: d.due_date || null,
});

const confirmation = (created, pending) => {
  const parts = [];
  if (created.length === 1) parts.push(`Created the task ${created[0]}.`);
  else if (created.length > 1) parts.push(`Created ${created.length} tasks.`);
  if (pending.length === 1) parts.push(`${pending[0]} was sent to the team owner for approval.`);
  else if (pending.length > 1) parts.push(`${pending.length} tasks were sent to the team owner for approval.`);
  return parts.join(' ');
};

// Transcript + editable draft cards; creates tasks in the chosen team's first Kanban column (To Do).
export const TaskDraftList = ({ capture, teams, user, onCreated, compact = false, showTranscript = true, announce = true }) => {
  const { drafts, transcript, updateDraft, removeDraft, clear } = capture;
  const [membersByTeam, setMembersByTeam] = useState({});
  const [creating, setCreating] = useState({});
  const [creatingAll, setCreatingAll] = useState(false);

  const teamKey = [...new Set(drafts.map((d) => d.team_id))].filter(Boolean).sort().join(',');
  const requestedRef = useRef(new Set());
  useEffect(() => {
    teamKey.split(',').filter((id) => id && !requestedRef.current.has(id)).forEach((id) => {
      requestedRef.current.add(id);
      commsApi.teamMembers(id)
        .then((list) => setMembersByTeam((p) => ({ ...p, [id]: list })))
        .catch(() => setMembersByTeam((p) => ({ ...p, [id]: [] })));
    });
  }, [teamKey]);

  // Returns 'created' | 'pending' | null so callers can build one spoken confirmation.
  const create = async (draft, { spoken = announce } = {}) => {
    if (!draft.title.trim()) { toast.error('Give the task a title first'); return null; }
    setCreating((c) => ({ ...c, [draft._id]: true }));
    try {
      await commsApi.createTask(toPayload(draft));
      const team = teams.find((t) => t.id === draft.team_id);
      const owner = team?.owner_id === user?.id;
      toast.success(owner ? `“${draft.title.trim()}” added to ${team?.name || 'the board'}` : `“${draft.title.trim()}” sent to the team owner for approval`);
      removeDraft(draft._id);
      onCreated?.(draft);
      const outcome = owner ? 'created' : 'pending';
      if (spoken) speak(confirmation(outcome === 'created' ? [draft.title.trim()] : [], outcome === 'pending' ? [draft.title.trim()] : []), { id: 'draft-confirm' }).catch(() => {});
      return outcome;
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not create “${draft.title}”`);
      return null;
    } finally {
      setCreating((c) => ({ ...c, [draft._id]: false }));
    }
  };

  const createAll = async () => {
    setCreatingAll(true);
    const created = [];
    const pending = [];
    for (const draft of drafts) { // sequential keeps approval order tidy
      const outcome = await create(draft, { spoken: false });
      if (outcome === 'created') created.push(draft.title.trim());
      if (outcome === 'pending') pending.push(draft.title.trim());
    }
    setCreatingAll(false);
    if (created.length + pending.length > 1) toast.success(`${created.length + pending.length} tasks created`);
    const summary = confirmation(created, pending);
    if (summary && announce) speak(summary, { id: 'draft-confirm' }).catch(() => {});
  };

  if (!drafts.length && !transcript) return null;

  return (
    <div className={`draft-panel ${compact ? 'compact' : ''}`} data-testid="task-drafts">
      {transcript && showTranscript && (
        <div className="draft-transcript" data-testid="voice-task-transcript">
          <Quote size={14} /> <span>{transcript}</span>
        </div>
      )}
      {drafts.length > 0 && (
        <>
          <div className="draft-toolbar">
            <span className="draft-count" data-testid="draft-count">{drafts.length} draft{drafts.length === 1 ? '' : 's'} — review, edit, then create</span>
            <div className="draft-toolbar-actions">
              <Button type="button" variant="outline" onClick={clear} disabled={creatingAll} data-testid="drafts-discard">
                <Trash2 size={16} /> Discard all
              </Button>
              <Button type="button" onClick={createAll} disabled={creatingAll} data-testid="drafts-create-all">
                {creatingAll ? <Loader2 size={16} className="animate-spin" /> : <CheckCheck size={16} />} Create all
              </Button>
            </div>
          </div>
          <div className="draft-list">
            {drafts.map((d, i) => (
              <TaskDraftCard
                key={d._id}
                draft={d}
                index={i}
                teams={teams}
                members={membersByTeam[d.team_id]}
                creating={Boolean(creating[d._id])}
                onChange={updateDraft}
                onRemove={removeDraft}
                onCreate={create}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
